import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createDocumentSelection } from '@/components/case-partner/hooks/useDocumentSelections';
import {
  DocumentSelector,
  placeholderDocument,
  type SelectionDisabledResult,
} from '@/components/document-selector/DocumentSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CUSTOM_CHRONOLOGY_DOCUMENT_CAP } from '@/hooks/useChronologies';
import { type Document } from '@/hooks/useDocuments';
import type { DocumentSelection } from '@/types';

interface ChronologyDocumentSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterSlug: string;
  selectedGuids: string[];
  selectedLabels?: Record<string, string>;
  onSave: (next: string[], labels: Record<string, string>) => void;
}

/** File picker that resolves to document GUIDs feeding a custom chronology. */
export function ChronologyDocumentSelectorDialog({
  open,
  onOpenChange,
  matterSlug,
  selectedGuids,
  selectedLabels = {},
  onSave,
}: ChronologyDocumentSelectorDialogProps) {
  const [draftFiles, setDraftFiles] = useState<Map<string, Document>>(new Map());

  const hydratedForOpenRef = useRef(false);
  const draftEditedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hydratedForOpenRef.current = false;
      draftEditedRef.current = false;
      return;
    }
    if (hydratedForOpenRef.current && draftEditedRef.current) return;
    const files = new Map<string, Document>();
    for (const guid of selectedGuids) {
      files.set(guid, placeholderDocument(guid, selectedLabels[guid] || guid));
    }
    setDraftFiles(files);
    hydratedForOpenRef.current = true;
  }, [open, selectedGuids, selectedLabels]);

  const selectedCount = draftFiles.size;
  const overLimit = selectedCount > CUSTOM_CHRONOLOGY_DOCUMENT_CAP;
  const atLimit = selectedCount >= CUSTOM_CHRONOLOGY_DOCUMENT_CAP;

  const documentSelections = useMemo<Map<string, DocumentSelection>>(() => {
    const map = new Map<string, DocumentSelection>();
    for (const [guid, doc] of draftFiles) {
      map.set(guid, createDocumentSelection(doc));
    }
    return map;
  }, [draftFiles]);

  const toggleFileSelection = useCallback((doc: Document) => {
    setDraftFiles((prev) => {
      if (prev.has(doc.guid)) {
        const next = new Map(prev);
        next.delete(doc.guid);
        draftEditedRef.current = true;
        return next;
      }
      if (prev.size >= CUSTOM_CHRONOLOGY_DOCUMENT_CAP) {
        return prev;
      }
      const next = new Map(prev);
      next.set(doc.guid, doc);
      draftEditedRef.current = true;
      return next;
    });
  }, []);

  const clearAllSelections = useCallback(() => {
    draftEditedRef.current = true;
    setDraftFiles(new Map());
  }, []);

  const isFileSelectionDisabled = useCallback(
    (doc: Document): SelectionDisabledResult => {
      if (doc.is_draft) {
        return {
          disabled: true,
          reason: 'Draft documents can’t be added to a chronology.',
        };
      }
      if (doc.processing_status && doc.processing_status !== 'completed') {
        return {
          disabled: true,
          reason: 'Only completed documents can feed a chronology.',
        };
      }
      if (doc.analysisSetEffective === false) {
        return {
          disabled: true,
          reason: 'Only documents in Deep Analysis can feed a chronology.',
        };
      }
      if (atLimit && !draftFiles.has(doc.guid)) {
        return {
          disabled: true,
          reason: `Custom chronologies support at most ${CUSTOM_CHRONOLOGY_DOCUMENT_CAP} documents.`,
        };
      }
      return { disabled: false };
    },
    [atLimit, draftFiles],
  );

  const handleSave = useCallback(() => {
    const labels: Record<string, string> = {};
    for (const [guid, doc] of draftFiles) {
      labels[guid] = doc.displayName || doc.fileName || guid;
    }
    onSave([...draftFiles.keys()], labels);
    onOpenChange(false);
  }, [draftFiles, onSave, onOpenChange]);

  const topBanner = (
    <p className="text-sm text-slate-700">
      <span className="font-medium">{selectedCount}</span>
      {' of '}
      {CUSTOM_CHRONOLOGY_DOCUMENT_CAP}
      {' documents selected.'}
      {overLimit && (
        <span className="text-red-600"> Reduce the selection to continue.</span>
      )}
    </p>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-gray-200 shrink-0">
          <DialogTitle className="text-lg font-semibold">Select documents</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1">
            Choose the documents that feed this chronology. Custom chronologies
            are capped at {CUSTOM_CHRONOLOGY_DOCUMENT_CAP} documents. Open a
            folder, or All Files, to load documents.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          <DocumentSelector
            matterSlug={matterSlug}
            documentSelections={documentSelections}
            onToggleSelection={toggleFileSelection}
            onClearSelection={clearAllSelections}
            showUploadButton={false}
            onDone={handleSave}
            doneLabel="Save"
            doneDisabled={selectedCount < 1 || overLimit}
            isSelectionDisabled={isFileSelectionDisabled}
            analysisSetOnly
            deferUntilFolderSelected
            excludeSections={['deadlines', 'tags', 'duplicate_locations', 'bundle_source']}
            topBanner={topBanner}
            className="h-full"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
